import {
  collection,
  doc,
  getDocs,
  query,
  runTransaction,
  serverTimestamp,
  where,
} from 'firebase/firestore';
import { db } from '../Firebase/Config';
import { CartItem } from './cartService';
import { claimSerialsFromArray, writeSerialStatus } from '../../utils/serialTracking';

function cleanUndefined(obj: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    out[k] = v !== undefined ? v : null;
  }
  return out;
}

/**
 * Mirrors the admin's allocateOrderToWarehouses logic.
 * Called immediately after the createOrder Cloud Function returns an orderId
 * so warehouse_inventory is deducted before the customer sees the confirmation.
 *
 * Picks the warehouse with the most stock for each item (greedy).
 * Sets isWarehouseAllocated: true and sourceWarehouseId on every order item.
 */
export async function reserveWarehouseStockForOrder(
  orderId: string,
  cartItems: CartItem[]
): Promise<void> {
  const warehousesSnap = await getDocs(
    query(collection(db, 'warehouses'), where('isActive', '==', true))
  );

  if (warehousesSnap.empty) {
    console.warn('reserveWarehouseStockForOrder: no active warehouses');
    return;
  }

  const warehouses = warehousesSnap.docs.map(d => ({
    id: d.id,
    name: (d.data().name as string) || d.id,
  }));

  const itemKey = (productId: string, variantId?: string | null) =>
    variantId ? `${productId}_${variantId}` : productId;

  const orderRef = doc(db, 'orders', orderId);

  await runTransaction(db, async (tx) => {
    const orderSnap = await tx.get(orderRef);
    const orderNumber = orderSnap.data()?.orderNumber;

    type InvRead = {
      ref: ReturnType<typeof doc>;
      quantity: number;
      minStockLevel: number;
      availableSerials?: string[];
      warehouseId: string;
      warehouseName: string;
      cartItem: CartItem;
    };

    const reads: InvRead[] = [];

    for (const item of cartItems) {
      const productPath = itemKey(item.productId, item.variantId);
      for (const wh of warehouses) {
        const ref = doc(db, 'warehouse_inventory', wh.id, 'products', productPath);
        const snap = await tx.get(ref);
        const qty = snap.exists() ? (snap.data()?.quantity ?? 0) : 0;
        if (qty > 0) {
          reads.push({
            ref,
            quantity: qty,
            minStockLevel: snap.data()?.minStockLevel ?? 0,
            availableSerials: snap.data()?.availableSerials,
            warehouseId: wh.id,
            warehouseName: wh.name,
            cartItem: item,
          });
        }
      }
    }

    // Group reads by item key
    const readsByItem = new Map<string, InvRead[]>();
    for (const r of reads) {
      const k = itemKey(r.cartItem.productId, r.cartItem.variantId);
      if (!readsByItem.has(k)) readsByItem.set(k, []);
      readsByItem.get(k)!.push(r);
    }

    // Validate stock for every item before touching anything
    for (const item of cartItems) {
      const k = itemKey(item.productId, item.variantId);
      const total = (readsByItem.get(k) ?? []).reduce((s, r) => s + r.quantity, 0);
      if (total < item.quantity) {
        throw new Error(
          `Insufficient stock for "${item.productName}". ` +
          `Available: ${total}, Requested: ${item.quantity}`
        );
      }
    }

    const updatedItems: Record<string, unknown>[] = [];

    for (const item of cartItems) {
      const k = itemKey(item.productId, item.variantId);
      const sorted = (readsByItem.get(k) ?? []).sort((a, b) => b.quantity - a.quantity);

      let remaining = item.quantity;

      for (const r of sorted) {
        if (remaining <= 0) break;
        const deduct = Math.min(r.quantity, remaining);
        const newQty = r.quantity - deduct;

        const updatePayload: Record<string, any> = {
          quantity: newQty,
          isOutOfStock: newQty === 0,
          isLowStock: newQty > 0 && newQty <= r.minStockLevel,
          lastUpdated: serverTimestamp(),
        };

        // Serial-tracked products: claim specific units instead of just
        // decrementing a count. Absent entirely for ordinary products.
        let claimedSerials: string[] = [];
        if (r.availableSerials) {
          const { claimed, remaining: remainingSerials } = claimSerialsFromArray(r.availableSerials, deduct);
          claimedSerials = claimed;
          updatePayload.availableSerials = remainingSerials;

          for (const serial of claimed) {
            writeSerialStatus(tx, serial, 'sold', {
              productId: item.productId,
              productName: item.productName,
              sku: item.variant?.sku,
              variantId: item.variantId ?? undefined,
              variantSku: item.variant?.sku,
              warehouseId: r.warehouseId,
              warehouseName: r.warehouseName,
              orderId,
              orderNumber,
            });
          }
        }

        tx.update(r.ref, updatePayload);

        const effectivePrice =
          item.discount && item.discount > 0
            ? item.price - (item.price * item.discount) / 100
            : item.price;

        updatedItems.push(
          cleanUndefined({
            productId: item.productId,
            productName: item.productName,
            productSku: item.variant?.sku ?? null,
            productImage: item.image ?? null,
            quantity: deduct,
            unitPrice: item.price,
            totalPrice: effectivePrice * deduct,
            variantId: item.variantId ?? null,
            variantSelections: null,
            variantSku: item.variant?.sku ?? null,
            sourceWarehouseId: r.warehouseId,
            sourceWarehouseName: r.warehouseName,
            serialNumbers: claimedSerials.length > 0 ? claimedSerials : null,
          })
        );

        remaining -= deduct;
      }
    }

    tx.update(orderRef, {
      items: updatedItems,
      isWarehouseAllocated: true,
      updatedAt: serverTimestamp(),
    });
  });
}
